/**
 * The website chat's embed code, in the one place it is written.
 *
 * Two screens hand it out — the Chatbot screen's Install section, where an
 * operator setting up an assistant looks for it, and the website inbox's own
 * settings, where the channel is configured — and two copies of a snippet is
 * how one of them comes to hand out a line the widget no longer reads.
 */
/**
 * The embed snippet. The CONSOLE origin serves widget.js, so it is composed
 * here from the console's own origin rather than serialized by the service,
 * which does not reliably know this origin. An audience is written as a line
 * of its own only when it is not the default: the default is chosen by
 * writing nothing, which is exactly the snippet every tenant already has.
 */
export const chatSnippet = ({
  websiteToken,
  consoleOrigin,
  audience = null,
}: {
  websiteToken: string;
  consoleOrigin: string;
  audience?: string | null;
}): string => {
  const config = audience
    ? `{ websiteToken: '${websiteToken}', audience: '${audience}' }`
    : `{ websiteToken: '${websiteToken}' }`;
  return [
    '<script>',
    `  window.$sphereChat = ${config};`,
    '</script>',
    `<script async src="${consoleOrigin}/widget.js"></script>`,
  ].join('\n');
};

export const BUTTON_SNIPPET = [
  '<!-- Any button or link on your page opens the chat: -->',
  '<button data-sphere-chat>Chat with us</button>',
  '',
  '<!-- Or from your own code: -->',
  '<!-- window.$sphereChat.open() / .close() / .toggle() -->',
].join('\n');

/**
 * The sign-out rides on the same listener rather than a line of its own: a
 * bare `setUser(null)` pasted into a page runs on every load. Sent for a
 * visitor who has only ever been anonymous it does nothing (the frame forgets
 * only once somebody has been signed in on that browser), which is what makes
 * the one-liner safe.
 */
export const IDENTITY_SNIPPET = [
  '// After your customer signs in (Firebase JS SDK), and when they sign out:',
  'firebase.auth().onIdTokenChanged(async (user) => {',
  '  window.$sphereChat.setUser(user ? await user.getIdToken() : null);',
  '});',
].join('\n');
